import { unified } from 'unified';
import parse from 'remark-parse';
import remarkMath from 'remark-math';
import remarkDirective from 'remark-directive';
import remarkGfm from 'remark-gfm';
import { visit } from 'unist-util-visit';
import { Root, Content } from 'mdast';
import { remarkDetails } from './remarkDetails';

export interface FootnoteDefinition {
  identifier: string;
  children: Content[];
}

export interface ChunkDocContext {
  footnotes: FootnoteDefinition[];
  root?: Root;
  prefix?: string;
}

export interface ChunkAST {
  nodes: Content[];
  docContext: ChunkDocContext;
}

function parseNumeric(value: string): number {
  let sanitized = value.replace(/,/g, '').trim();
  if (sanitized.endsWith('%')) {
    sanitized = sanitized.slice(0, -1).trim();
    const num = parseFloat(sanitized);
    return isNaN(num) ? 0 : num / 100;
  } else {
    const num = parseFloat(sanitized);
    return isNaN(num) ? 0 : num;
  }
}

// Add this function to your parseMarkdown.ts
function remarkMergedCells() {
  return (tree: any) => {
    visit(tree, 'table', (table: any) => {
      if (!table.children || table.children.length === 0) return;
      
      // Process each row
      for (let rowIndex = 0; rowIndex < table.children.length; rowIndex++) {
        const row = table.children[rowIndex];
        
        // Process each cell in the row
        for (let cellIndex = 0; cellIndex < row.children.length; cellIndex++) {
          const cell = row.children[cellIndex];
          
          // Initialize data properties for each cell
          cell.data = cell.data || {};
          cell.data.hProperties = cell.data.hProperties || {};
          
          // Check for rowspan - look at cells below
          let rowspan = 1;
          for (let i = rowIndex + 1; i < table.children.length; i++) {
            const rowBelow = table.children[i];
            if (cellIndex < rowBelow.children.length) {
              const cellBelow = rowBelow.children[cellIndex];
              
              // Check if cell below is empty (indicating a merged cell)
              const isEmpty = cellBelow.children.length === 0 || 
                (cellBelow.children.length === 1 && 
                 cellBelow.children[0].type === 'text' && 
                 cellBelow.children[0].value.trim() === '');
              
              if (isEmpty) {
                rowspan++;
                // Mark the cell as part of a merge
                cellBelow.data = cellBelow.data || {};
                cellBelow.data.merged = true;
              } else {
                break;
              }
            }
          }
          
          // If rowspan > 1, set it on the cell
          if (rowspan > 1) {
            cell.data.hProperties.rowspan = rowspan;
          }
          
          // Check for colspan - look at cells to the right
          let colspan = 1;
          for (let i = cellIndex + 1; i < row.children.length; i++) {
            const cellRight = row.children[i];
            
            // Check if cell to the right is empty (indicating a merged cell)
            const isEmpty = cellRight.children.length === 0 || 
              (cellRight.children.length === 1 && 
               cellRight.children[0].type === 'text' && 
               cellRight.children[0].value.trim() === '');
            
            if (isEmpty) {
              colspan++;
              // Mark the cell as part of a merge
              cellRight.data = cellRight.data || {};
              cellRight.data.merged = true;
            } else {
              break;
            }
          }
          
          // If colspan > 1, set it on the cell
          if (colspan > 1) {
            cell.data.hProperties.colspan = colspan;
          }
        }
      }
      
      // Remove empty cells that are part of merged cells
      for (let rowIndex = 0; rowIndex < table.children.length; rowIndex++) {
        const row = table.children[rowIndex];
        row.children = row.children.filter((cell: any) => !cell.data?.merged);
      }
    });
  };
}

export function parseAndSplitByH2(rawMarkdown: string, prefix?: string): ChunkAST[] {
  const processor = unified()
    .use(parse)
    .use(remarkGfm)
    .use(remarkMath, { singleDollarTextMath: false })
    .use(remarkDirective)
    .use(remarkMergedCells)
    .use(remarkDetails);

  const rawAst = processor.parse(rawMarkdown);
  const tree = processor.runSync(rawAst) as Root;

  const chunks: ChunkAST[] = [];
  let currentNodes: Content[] = [];
  const footnoteDefs: FootnoteDefinition[] = [];


  visit(tree, 'footnoteDefinition', (node: any) => {
    footnoteDefs.push({
      identifier: prefix ? `${prefix}-${node.identifier}` : node.identifier,
      children: node.children
    });
  });


  // We currently don't use text directives. If we ever try to, this will break them.
  // If we start using them, we'll need some other way of handling colons in the text.
  // @ts-expect-error The types seem not to match up quite right (perhaps because of the remark-directive plugin)
  visit(tree, 'textDirective', (node: any, index: number, parent: any) => {
    parent.children[index] = {
      type: 'text',
      value: `:${node.name}`
    };
  });

  tree.children.forEach((node: Content) => {
    if (node.type === 'footnoteDefinition') return;
    if (node.type === 'heading' && node.depth === 2) {
      if (currentNodes.length > 0) {
        chunks.push({
          nodes: currentNodes,
          docContext: {
            footnotes: footnoteDefs,
            root: tree,
            prefix: prefix
          }
        });
      }
      currentNodes = [node];
    } else {
      currentNodes.push(node);
    }
  });

  if (currentNodes.length > 0) {
    chunks.push({
      nodes: currentNodes,
      docContext: {
        footnotes: footnoteDefs,
        root: tree,
        prefix: prefix
      }
    });
  }

  return chunks;
}